import { inject } from '@angular/core';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { pipe, switchMap, tap } from 'rxjs';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { IWHWorkOrderTemplateDTO, WHWorkObjectTemplateDOM, WHWorkObjectTemplatePreviewDOM, initHttpParams } from '@workheld/workheld-shared-lib';
import { WorkObjectTemplateAsyncService } from 'src/app/app-services-async/work-object-template-async.service';

type UseWorkObjectTemplateDialogState = {
  templates: WHWorkObjectTemplateDOM[];
  workOrderTemplates: IWHWorkOrderTemplateDTO[];
  totalElements: number;
  isLoading: boolean;
  isLoadingWorkOrders: boolean;
  filters: {
    term: string;
    page: number;
    sort: string;
    sortMode: 'ASC' | 'DESC';
    pageSize: number;
  };
};

const initialState: UseWorkObjectTemplateDialogState = {
  templates: [],
  workOrderTemplates: [],
  totalElements: 0,
  isLoading: false,
  isLoadingWorkOrders: false,
  filters: {
    term: '',
    page: 0,
    sort: 'templateName',
    sortMode: 'ASC',
    pageSize: 20,
  },
};

export const UseWorkObjectTemplateDialogStore = signalStore(
  withState(initialState),
  withMethods(
    (
      store,
      templateService = inject(WorkObjectTemplateAsyncService),
    ) => ({
      setSearchTerm(term: string): void {
        patchState(store, (state) => ({
          filters: { ...state.filters, term: term ?? '', page: 0 },
        }));
      },

      setPage(page: number): void {
        patchState(store, (state) => ({
          filters: { ...state.filters, page },
        }));
      },

      setPageSize(pageSize: number): void {
        patchState(store, (state) => ({
          filters: { ...state.filters, pageSize, page: 0 },
        }));
      },

      clearWorkOrderTemplates(): void {
        patchState(store, { workOrderTemplates: [], isLoadingWorkOrders: false });
      },

      loadWorkOrderTemplates: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { isLoadingWorkOrders: true, workOrderTemplates: [] })),
          switchMap((workObjectTemplateId) =>
            templateService.getWorkOrderTemplatesForWorkObjectTemplate(workObjectTemplateId).pipe(
              tapResponse({
                next: (workOrderTemplates) => {
                  patchState(store, { workOrderTemplates, isLoadingWorkOrders: false });
                },
                error: () => {
                  patchState(store, { isLoadingWorkOrders: false });
                },
              }),
            ),
          ),
        ),
      ),

      loadTemplates: rxMethod<UseWorkObjectTemplateDialogState['filters']>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap((filters) => {
            const { page, sort, sortMode, pageSize, term } = filters;
            const httpParams = initHttpParams({
              page,
              sort,
              sortMode,
              size: pageSize,
              term: term?.trim() || undefined,
            });

            return templateService.getWorkObjectTemplatePaged(httpParams).pipe(
              tapResponse({
                next: (response) => {
                  const preview = new WHWorkObjectTemplatePreviewDOM(response);
                  patchState(store, {
                    templates: preview.workObjectTemplates,
                    totalElements: preview.numTotalEntries,
                    isLoading: false,
                  });
                },
                error: () => {
                  patchState(store, { isLoading: false });
                },
              }),
            );
          }),
        ),
      ),
    }),
  ),
  withHooks({
    onInit(store) {
      store.loadTemplates(store.filters);
    },
  }),
);
